/**
 * GradedPlay search and show – user IDs, group memberships, group IDs.
 * All index/delete and search go through Cloud Functions (src/api/algoliaApi.js).
 * No Algolia write or search keys on the frontend.
 *
 * - Teammates: search by name only, same group; email/phone private and not returned.
 * - Find by contact: search by email or phone (no group filter).
 * - Index: objectID (uid), name, email, phone, groupIds (for filtering).
 */

import { getAlgoliaAppId } from '../config/algolia';
import * as algoliaApi from '../api/algoliaApi';

/**
 * Build Algolia record from Firestore user and approved group IDs.
 * Email and phone are stored for "find by contact" but never returned in teammate search.
 */
export function userToAlgoliaRecord(user, groupIds = []) {
  if (!user || !user.id) return null;
  const uid = user.id;
  const name = (user.displayName || user.name || user.email || '').toString().trim();
  const email = (user.email || '').toString().trim().toLowerCase();
  const phone = (user.phone || '').toString().trim();
  const groupIdsList = Array.isArray(groupIds) ? groupIds : [];
  return {
    objectID: uid,
    name: name || uid,
    email,
    phone,
    groupIds: groupIdsList,
    updatedAt: Math.floor(Date.now() / 1000),
  };
}

/**
 * Index one user to Algolia via backend. Call after profile update or group membership change.
 */
export async function saveUserToAlgolia(user, groupIds = []) {
  const record = userToAlgoliaRecord(user, groupIds);
  if (!record) return;
  try {
    await algoliaApi.userIndex(record);
  } catch (err) {
    console.error('Algolia save user error:', err);
  }
}

/**
 * Remove user from Algolia via backend (e.g. account deleted).
 */
export async function deleteUserFromAlgolia(uid) {
  if (!uid) return;
  try {
    await algoliaApi.userDelete(uid);
  } catch (err) {
    console.error('Algolia delete user error:', err);
  }
}

/**
 * Search teammates by name only via backend. Same group; email/phone not returned.
 */
export async function searchTeammates(searcherGroupIds, query, options = {}) {
  try {
    return await algoliaApi.searchTeammates(searcherGroupIds || [], query || '', options);
  } catch (err) {
    console.error('Algolia search teammates error:', err);
    return [];
  }
}

/**
 * Find user by email or phone via backend. Returns name and contact.
 */
export async function findUserByEmailOrPhone(query, options = {}) {
  const q = (query || '').trim();
  if (!q) return [];
  try {
    return await algoliaApi.findUserByEmailOrPhone(q, options);
  } catch (err) {
    console.error('Algolia find user by contact error:', err);
    return [];
  }
}

/**
 * Sync all users to Algolia via backend. Call from Admin or a one-time script.
 */
export async function syncAllUsersToAlgolia(usersList, getApprovedGroupIds) {
  let synced = 0;
  let errors = 0;
  for (const user of usersList || []) {
    if (!user?.id) continue;
    try {
      const groupIds = typeof getApprovedGroupIds === 'function' ? getApprovedGroupIds(user.id) || [] : [];
      await saveUserToAlgolia(
        { id: user.id, displayName: user.displayName, email: user.email, phone: user.phone },
        groupIds
      );
      synced++;
    } catch (err) {
      console.error('Algolia sync user error:', err);
      errors++;
    }
  }
  return { synced, errors };
}

/** True when Algolia users index is available (backend handles search; appId used for UI only). */
export function isAlgoliaUsersConfigured() {
  return !!getAlgoliaAppId();
}
